/**
 * 集中管理「店家是否在線」狀態，供 Home / Checkout / Cart 等多頁使用。
 * - 訂閱心跳頻道後以 Socket 發送 user:checkShop，由 OrderSocket 查 Redis 並回傳心跳/離線事件
 * - 多個頁面 watch 同一店家時只訂閱一次（ref count）
 * - 消費者端心跳：定時發送 user:heartbeat 維持連線／可選由 OrderSocket 記錄用戶在線
 */
import { reactive } from 'vue'
import { Global } from '@/Service/GlobalService'

const HEARTBEAT_OFFLINE_MS = 15000   // 逾時未收到心跳則視為離線（略大於 OrderSocket Redis TTL 10s）
const HEARTBEAT_OFFLINE_MS_FROM_LIST = 20000  // 來自 API 列表時略放寬，避免 WebSocket 短暫中斷就消失
const CHECK_SHOP_RESPONSE_MS = 2500  // 發送 user:checkShop 後等待 OrderSocket 回傳的最長時間

/** 消費者端心跳間隔（毫秒），從 .env VUE_APP_HEARTBEAT_INTERVAL_SEC 讀取，預設 1 秒 */
const USER_HEARTBEAT_INTERVAL_MS = (parseInt(process.env.VUE_APP_HEARTBEAT_INTERVAL_SEC, 10) || 1) * 1000

/** 店家在線／載入中狀態（reactive），供元件讀取，Vue 會追蹤依賴 */
export const state = reactive({
  shopOnlineMap: {},  // shopId -> boolean
  shopLoadingMap: {}  // shopId -> boolean（等待回傳時為 true）
})

const channels = {}
const heartbeatTimeouts = {}
const firstHeartbeatTimeouts = {}
const checkResponseTimeouts = {}  // user:checkShop 後等待回傳的 timeout
const watcherCount = {}

/** 消費者端心跳定時器 */
let userHeartbeatTimer = null

/** 設定心跳逾時：逾時未收到心跳則將該店家標記為離線 */
function setHeartbeatTimeout(shopId, ms = HEARTBEAT_OFFLINE_MS) {
  if (heartbeatTimeouts[shopId]) clearTimeout(heartbeatTimeouts[shopId])
  heartbeatTimeouts[shopId] = setTimeout(() => {
    state.shopOnlineMap[shopId] = false
    heartbeatTimeouts[shopId] = null
  }, ms)
}

/** 清除「等待首次心跳」的計時器（收到心跳或離線時呼叫） */
function clearFirstHeartbeatTimeout(shopId) {
  if (firstHeartbeatTimeouts[shopId]) {
    clearTimeout(firstHeartbeatTimeouts[shopId])
    firstHeartbeatTimeouts[shopId] = null
  }
}

/** 清除「等待 user:checkShop 回傳」的計時器 */
function clearCheckResponseTimeout(shopId) {
  if (checkResponseTimeouts[shopId]) {
    clearTimeout(checkResponseTimeouts[shopId])
    checkResponseTimeouts[shopId] = null
  }
}

/** 訂閱該店家的心跳與離線頻道：收到心跳則標為在線並重設逾時，收到離線則標為離線 */
function subscribeChannel(id) {
  if (channels[id]) return
  const channelName = 'UserOrderShopChannel.' + id
  const channel = window.Echo.channel(channelName)
  channel.listen('.ShopHeartbeatEvent', (e) => {
    if (e.shopId !== id) return
    clearCheckResponseTimeout(id)
    state.shopOnlineMap[id] = true
    state.shopLoadingMap[id] = false
    if (!Global.lastShopOnlineAt) Global.lastShopOnlineAt = {}
    Global.lastShopOnlineAt[id] = Date.now()
    clearFirstHeartbeatTimeout(id)
    setHeartbeatTimeout(id)
  })
  channel.listen('.ShopWentOfflineEvent', (e) => {
    if (e.shopId !== id) return
    clearCheckResponseTimeout(id)
    state.shopOnlineMap[id] = false
    state.shopLoadingMap[id] = false
    clearFirstHeartbeatTimeout(id)
    if (heartbeatTimeouts[id]) {
      clearTimeout(heartbeatTimeouts[id])
      heartbeatTimeouts[id] = null
    }
  })
  channels[id] = channel
}

/** WebSocket 重連後，將目前監聽中的店家暫設為在線並重設逾時，避免誤判離線（只綁定一次） */
function bindReconnectIfNeeded() {
  const socket = window.Echo?.connector?.socket
  if (!socket || socket._shopReconnectBound) return
  socket._shopReconnectBound = true
  socket.on('connect', () => {
    Object.keys(channels).forEach(id => {
      const numId = Number(id)
      state.shopOnlineMap[numId] = true
      if (!Global.lastShopOnlineAt) Global.lastShopOnlineAt = {}
      Global.lastShopOnlineAt[numId] = Date.now()
      setHeartbeatTimeout(numId)
    })
  })
}

/** 發送消費者端心跳（user:heartbeat），供 OrderSocket 維持連線或記錄在線 */
function sendUserHeartbeat() {
  const socket = window.Echo?.connector?.socket
  if (!socket?.connected) return
  const isGuest = Global.isGuest === true
  socket.emit('user:heartbeat', {
    userId: Global.User?.id ?? null,
    type: isGuest ? 'guest' : 'user',
    is_guest: isGuest,
  })
}

/** 啟動消費者端定時心跳（與 OrderShop 間隔一致，可由 .env 設定） */
export function startUserHeartbeat() {
  clearUserHeartbeat()
  sendUserHeartbeat()
  userHeartbeatTimer = setInterval(sendUserHeartbeat, USER_HEARTBEAT_INTERVAL_MS)
  const socket = window.Echo?.connector?.socket
  if (socket && !socket._userHeartbeatReconnectBound) {
    socket._userHeartbeatReconnectBound = true
    socket.on('connect', sendUserHeartbeat)
  }
}

/** 清除消費者端心跳定時器 */
export function clearUserHeartbeat() {
  if (userHeartbeatTimer) {
    clearInterval(userHeartbeatTimer)
    userHeartbeatTimer = null
  }
}

/** 開始監聽該店家在線狀態：ref count 管理，首次時打 API 或依 fromList 直接訂閱心跳；多頁同時 watch 只訂閱一次 */
export function watchShop(shopId, options = {}) {
  if (!shopId) return
  const id = Number(shopId)
  watcherCount[id] = (watcherCount[id] || 0) + 1
  if (watcherCount[id] > 1) return

  if (options.fromList) {
    state.shopOnlineMap[id] = true
    state.shopLoadingMap[id] = false
    if (!Global.lastShopOnlineAt) Global.lastShopOnlineAt = {}
    Global.lastShopOnlineAt[id] = Date.now()
    setHeartbeatTimeout(id, HEARTBEAT_OFFLINE_MS_FROM_LIST)
    subscribeChannel(id)
    bindReconnectIfNeeded()
    return
  }

  state.shopLoadingMap[id] = true
  subscribeChannel(id)
  bindReconnectIfNeeded()

  const socket = window.Echo?.connector?.socket
  if (socket?.connected) {
    socket.emit('user:checkShop', { shopId: id })
    checkResponseTimeouts[id] = setTimeout(() => {
      checkResponseTimeouts[id] = null
      if (state.shopLoadingMap[id] !== false) {
        state.shopOnlineMap[id] = false
        state.shopLoadingMap[id] = false
      }
    }, CHECK_SHOP_RESPONSE_MS)
  } else {
    state.shopOnlineMap[id] = false
    state.shopLoadingMap[id] = false
  }
}

/** 停止監聽該店家：ref count 減一，為 0 時離開頻道並清除狀態與計時器 */
export function unwatchShop(shopId) {
  if (!shopId) return
  const id = Number(shopId)
  watcherCount[id] = (watcherCount[id] || 1) - 1
  if (watcherCount[id] > 0) return

  clearFirstHeartbeatTimeout(id)
  clearCheckResponseTimeout(id)
  if (heartbeatTimeouts[id]) {
    clearTimeout(heartbeatTimeouts[id])
    heartbeatTimeouts[id] = null
  }
  if (channels[id]) {
    window.Echo.leave('UserOrderShopChannel.' + id)
    delete channels[id]
  }
  delete state.shopOnlineMap[id]
  delete state.shopLoadingMap[id]
  delete watcherCount[id]
}

/** 取得該店家是否在線（從 reactive state 讀取，供 Vue 元件依賴追蹤） */
export function getShopOnline(shopId) {
  if (!shopId) return false
  return state.shopOnlineMap[Number(shopId)] === true
}

/** 該店家是否尚在取得在線狀態（API 尚未回傳前為 true，用於顯示「讀取中」） */
export function isShopLoading(shopId) {
  if (!shopId) return false
  return state.shopLoadingMap[Number(shopId)] === true
}

/** 對外介面：state、watchShop、unwatchShop、getShopOnline、isShopLoading、startUserHeartbeat、clearUserHeartbeat */
export default {
  state,
  watchShop,
  unwatchShop,
  getShopOnline,
  isShopLoading,
  startUserHeartbeat,
  clearUserHeartbeat
}
